import { Check, ChevronsUpDown } from 'lucide-react';
import { Link, useRouteLoaderData } from 'react-router';
import type { Club } from '@repo/shared-types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@repo/ui/components/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@repo/ui/components/sidebar';
import { id as routeAppLayout } from '@/layout/app-layout';
import { urlControlPanel } from '@/urls';
import { cn } from '@repo/ui/lib/utils';

export function ClubLogo({
  club,
  className,
}: {
  club: Pick<Club, 'name' | 'logo'>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex aspect-square size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-primary text-primary-foreground',
        className
      )}
    >
      {club.logo ? (
        <img src={club.logo} alt={club.name} className="size-full object-cover" />
      ) : (
        <span className="text-sm font-semibold">{club.name.charAt(0).toUpperCase()}</span>
      )}
    </div>
  );
}

export function ClubSwitcher({ club }: { club: Club }) {
  const { clubs } = useRouteLoaderData(routeAppLayout) as { clubs: Club[] };

  const summary = (
    <>
      <ClubLogo club={club} />
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate font-semibold">{club.name}</span>
        <span className="truncate text-xs text-muted-foreground">{club.city || 'Club'}</span>
      </div>
    </>
  );

  if (clubs.length < 2) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" asChild>
            <div>{summary}</div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent">
              {summary}
              <ChevronsUpDown className="ml-auto group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-56" align="start" side="bottom" sideOffset={4}>
            <DropdownMenuLabel>Clubes</DropdownMenuLabel>
            {clubs.map((item) => (
              <DropdownMenuItem key={item.id} asChild>
                <Link to={urlControlPanel(item.id)} viewTransition>
                  <ClubLogo club={item} />
                  <span className="flex-1 truncate">{item.name}</span>
                  {item.id === club.id && <Check />}
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
